import { formatDate, isToday, isTomorrow } from 'date-fns'
import { computed } from 'vue'
import {
  THour,
  TMinute,
  TDay,
  TMonth,
  TYear,
  TDateISODate,
  TDateISOTime
} from '@/interfaces'

export function formatDurationWords(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)

  if (hours === 0) {
    return `${minutes} min${minutes === 1 ? '' : 's'}`
  } else if (hours === 1 && minutes === 0) {
    return `1 hr`
  } else if (hours >= 1 && minutes > 0) {
    return `${hours} hr${hours > 1 ? 's' : ''} ${minutes} min${
      minutes > 1 ? 's' : ''
    }`
  } else {
    return `${hours} hrs`
  }
}

export function useTimeRemainingHelper(getSeconds: () => number) {
  const timeRemaining = computed(() => {
    const seconds = getSeconds()

    if (seconds === 0) {
      return '-'
    }
    return formatDurationWords(seconds + 60)
  })

  return { timeRemaining }
}

export function useFormatDateStampHelper() {
  function formatDateStamp(dateStamp: string | null) {
    if (dateStamp) {
      const date = new Date(dateStamp)
      const formattedTime = formatISOTime(date)
      let day: string

      if (isToday(date)) {
        day = 'today'
      } else if (isTomorrow(date)) {
        day = 'tomorrow'
      } else {
        const dayOfWeek = formatDate(date, 'EEEE')
        day = `on ${dayOfWeek}`
      }
      return `${formattedTime} ${day}`
    }
    return '-'
  }

  return { formatDateStamp }
}

/**
 * Formats a Date object into a formatted date string.
 *
 * @param {Date} date - The Date object to format.
 * @param {FormatFullDateOptions} [options] - Optional formatting options.
 * @param {('day' | 'year')[]} [options.with] - An array specifying which optional components to include ('day' or 'year').
 * @returns {string} The formatted date string (e.g., "18 Sept", "18 Sept 2024", "Wed, 18 Sept", or "Wed, 18 Sept 2024").
 */
type WithTypes = 'day' | 'year'
interface FormatFullDateOptions {
  with?: WithTypes[]
  timeZone?: string
}
export function formatFullDate(
  date: Date,
  options?: FormatFullDateOptions
): string {
  const formatOptions: Intl.DateTimeFormatOptions = {
    weekday: options?.with?.includes('day') ? 'short' : undefined,
    day: 'numeric',
    month: 'short',
    year: options?.with?.includes('year') ? 'numeric' : undefined,
    timeZone: options?.timeZone ?? 'UTC'
  }
  const fullDateFormatter = new Intl.DateTimeFormat('en-GB', formatOptions)

  let formattedDate = fullDateFormatter.format(date)

  if (options?.with?.includes('day') && !formattedDate.includes(',')) {
    const parts = formattedDate.split(' ')
    if (parts.length > 1) {
      formattedDate = `${parts[0]}, ${parts.slice(1).join(' ')}`
    }
  }

  return formattedDate
}

/**
 * Formats a Date object into an ISO 8601 date string (YYYY-MM-DD).
 *
 * @param {Date} date - The Date object to format.
 * @returns {TDateISODate} The formatted date string in ISO 8601 format (e.g., "2024-09-18").
 */
export function formatISODate(date: Date): TDateISODate {
  const year = date.getFullYear().toString() as TYear
  const month = (date.getMonth() + 1).toString().padStart(2, '0') as TMonth
  const day = date.getDate().toString().padStart(2, '0') as TDay

  return `${year}-${month}-${day}`
}

/**
 * Formats a Date object into an ISO 8601 extended time string (HH:MM).
 *
 * @param {Date} date - The Date object to format.
 * @returns {TDateISOTime} The formatted time string in ISO 8601 format (e.g., "07:49").
 */
export function formatISOTime(date: Date): TDateISOTime {
  const hours = date.getHours().toString().padStart(2, '0') as THour
  const minutes = date.getMinutes().toString().padStart(2, '0') as TMinute

  return `${hours}:${minutes}`
}

/**
 * Rounds a time string in ISO 8601 format (HH:MM) to the nearest quarter hour.
 *
 * @param {TDateISOTime} time - The time string to round, in "HH:MM" format.
 * @returns {TDateISOTime} The rounded time string, in "HH:MM" format.
 *
 * @example
 * roundToQuarterHour("10:07"); // Returns "10:15"
 * roundToQuarterHour("10:23"); // Returns "10:30"
 * roundToQuarterHour("10:44"); // Returns "10:45"
 * roundToQuarterHour("10:46"); // Returns "11:00"
 */
export function roundToQuarterHour(time: TDateISOTime): TDateISOTime {
  const [hours, minutes] = time.split(':').map(Number)
  const date = new Date()
  date.setHours(hours, minutes, 0, 0)

  const roundedMinutes = Math.ceil(date.getMinutes() / 15) * 15
  date.setMinutes(roundedMinutes)

  const roundedHours = date.getHours()
  const roundedMins = date.getMinutes()

  return `${String(roundedHours).padStart(2, '0')}:${String(roundedMins).padStart(2, '0')}` as TDateISOTime
}

/**
 * Converts an ISO 8601 date to a more readable format.
 *
 * @param {TDateISOTime} isoString - The time string.
 * @param {string} [timeZone] - An IANA time zone to render the date in. Defaults to the device's own time zone.
 * @returns {string} A formatted string in the "DD Month at HH:MM" format.
 *
 * @example
 * formatReadableDate("2025-07-31T18:00:00.000Z"); // returns "31 July at 18:00"
 */
export function formatReadableDate(
  isoString: string,
  timeZone?: string
): string {
  const date = new Date(isoString)

  if (Number.isNaN(date.getTime())) {
    return ''
  }

  if (timeZone) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone
    }).formatToParts(date)

    const partValue = (type: string) =>
      parts.find((part) => part.type === type)?.value ?? ''

    return `${partValue('day')} ${partValue('month')} at ${partValue('hour')}:${partValue('minute')}`
  }

  const monthNames: string[] = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ]

  const day = date.getDate()
  const month = monthNames[date.getMonth()]
  const hours = date.getHours()
  const minutes = date.getMinutes()

  const paddedMinutes = String(minutes).padStart(2, '0')
  const paddedHours = String(hours).padStart(2, '0')

  return `${day} ${month} at ${paddedHours}:${paddedMinutes}`
}

/**
 * Resolves the IANA time zone to use for a property, falling back to the
 * viewing device's own time zone when the property doesn't have one set.
 *
 * @param {string | null} [timeZone] - The property's IANA time zone, if known.
 * @returns {string} An IANA time zone name.
 */
export function getPropertyTimeZone(timeZone?: string | null): string {
  if (timeZone) {
    return timeZone
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone
}

export function isPeakHour(date: Date = new Date()): boolean {
  const hour = Number(
    new Intl.DateTimeFormat('en-ZA', {
      hour: 'numeric',
      hour12: false,
      timeZone: 'Africa/Johannesburg'
    }).format(date)
  )

  return (hour >= 7 && hour <= 10) || (hour >= 18 && hour <= 20)
}
